import { DocsHRResponse } from 'core/API/documents';
import { useCachedQueryData } from 'core/hooks/useCachedQueryData';
import { IUsers } from 'core/interfaces/userInterface';
import { Formik, FormikProps, useFormik } from 'formik';
import { useWorkplaceUser } from 'module/documents/utils';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import {
	createContext,
	ReactNode,
	useContext,
	useState,
	Dispatch,
	SetStateAction,
	useEffect,
} from 'react';
import {
	getEmployeeI9Form,
	getEmployeePciComplianceForm,
	getEmployeeW4Form,
	getSupplementFormsementForms,
} from 'core/API/employeeForms';
import { formatDateToMMDDYYYY } from 'shared/utils/dates/formatDate';
import { User } from '../../types';

interface NewEmployeeType {
	firstName: string;
	middleName: string;
	lastName: string;
	email: string;
}

// Define the types for the context value
interface DocumentContextType {
	formik: FormikProps<any>;
	resetForm: (newValues: any) => void;
	isEmployer: boolean;
	selectedEmployee: User;
	setSelectedEmployee: React.Dispatch<React.SetStateAction<User>>;
	loadedFormIds: Record<string, any>;
	recordFormSubmission: (formId: string) => void;
	setLoadedFormIds: React.Dispatch<React.SetStateAction<Record<string, any>>>;
	newEmployee: NewEmployeeType;
	setNewEmployee: React.Dispatch<React.SetStateAction<NewEmployeeType>>;
	newEmployeeCreated: boolean;
	setNewEmployeeCreated: React.Dispatch<React.SetStateAction<boolean>>;
	handleModalClose: () => void;
	open: boolean;
	setOpen: Dispatch<SetStateAction<boolean>>;
	handleOpen: () => void;
	startDate: string;
	setStartDate: Dispatch<SetStateAction<string>>;
	endDate: string;
	setEndDate: Dispatch<SetStateAction<string>>;
	businessWeekStart: string;
	setBusinessWeekStart: Dispatch<SetStateAction<string>>;
	scheduleView: string;
	setScheduleView: Dispatch<SetStateAction<string>>;
	activeTabIndex: number;
	setActiveTabIndex: React.Dispatch<React.SetStateAction<number>>;
	submittedForms: Record<string, boolean>;
	setSubmittedForms: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
	employeeState: string;
	setEmployeeState: React.Dispatch<React.SetStateAction<string>>;
	translatorSignatureFile: File | null;
	setTranslatorSignatureFile: React.Dispatch<React.SetStateAction<File | null>>;
	editSupplementB: boolean;
	setEditSupplementB: React.Dispatch<React.SetStateAction<boolean>>;
	resetDocumentContext: (newState?: string) => void;
	lastLoadedEmployeeId: number | null;
	setLastLoadedEmployeeId: Dispatch<SetStateAction<number | null>>;
}

// Create the context
const DocumentContext = createContext<DocumentContextType | null>(null);

// Create a custom hook for using the DocumentContext
export const useDocumentContext = (): DocumentContextType => {
	const context = useContext(DocumentContext);
	if (!context) {
		throw new Error('useDocumentContext must be used within a DocumentProvider');
	}
	return context;
};

const now = new Date().toISOString().split('T')[0];

interface NewEmployeeType {
	firstName: string;
	middleName: string;
	lastName: string;
	email: string;
}

// Define the provider component
interface DocumentProviderProps {
	children: ReactNode;
	defaultUser: User;

	newEmployeeState: [NewEmployeeType, Dispatch<SetStateAction<NewEmployeeType>>];
}

export const DocumentProvider = ({
	children,
	defaultUser,
	newEmployeeState,
}: DocumentProviderProps) => {
	const [selectedEmployee, setSelectedEmployee] = useState<User>(defaultUser);
	const [loadedFormIds, setLoadedFormIds] = useState<Record<string, any>>({});
	const [newEmployee, setNewEmployee] = newEmployeeState;
	const { workplace } = useWorkplacesContext();
	const { data: user } = useWorkplaceUser();
	const [open, setOpen] = useState(false);
	const handleOpen = () => setOpen(true);
	const handleModalClose = () => setOpen(false);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [businessWeekStart, setBusinessWeekStart] = useState('');
	const [scheduleView, setScheduleView] = useState('scheduler');
	const [activeTabIndex, setActiveTabIndex] = useState(0);
	const [translatorSignatureFile, setTranslatorSignatureFile] = useState<File | null>(null);
	const [lastLoadedEmployeeId, setLastLoadedEmployeeId] = useState<number | null>(null);
	const [submittedForms, setSubmittedForms] = useState<Record<string, boolean>>({
		common_fields_submitted: false,
		i9_form_submitted: false,
		i9_supplement_a_submitted: false,
		i9_supplement_b_submitted: false,
		w4_form_submitted: false,
		notice_tipped_employees_submitted: false,
		mi_w4_form_submitted: false,
		food_report_agreements_submitted: false,
		w2_consent_form_submitted: false,
		pci_acknowledgment_submitted: false,
		direct_deposit_form_submitted: false,
		full_variable_time_form_submitted: false,
		i_understand_form_submitted: false,
		human_resource_data_submitted: false,
		select_employee_submitted: false,
	});
	const [translatorDetails, setTranslatorDetails] = useState<any>(null);
	const [i9FormDetails, setI9FormDetails] = useState<any>(null);
	const [W4FormDetails, setW4FormDetails] = useState<any>(null);
	const [pciFormDetails, setPciFormDetails] = useState<any>(null);
	const isEmployer = false;
	// Commented out - employee-form API calls not needed
	// const hrDetails = useCachedQueryData<DocsHRResponse>('employee-form/hr');
	const hrDetails = null;
	useEffect(() => {
		const fetchData = async () => {
			if (selectedEmployee?.id) {
				try {
					const [w4Data] = await Promise.all([
						// getEmployeeI9Form(selectedEmployee.id), // Commented out - API returns 404, not needed
						getEmployeeW4Form(selectedEmployee.id),
						// getEmployeePciComplianceForm(selectedEmployee.id), // Commented out - API returns 404, not needed
					]);

					// setI9FormDetails(i9Data);
					// setW4FormDetails(w4Data);
					// setPciFormDetails(pciData);

					// Step 2: Once i9Data is available, fetch Supplement Translator Form
					// Commented out - depends on i9Data which is no longer fetched
					// if (i9Data?.id) {
					// 	const translatorData = await getSupplementFormsementForms(i9Data.id);
					// 	setTranslatorDetails(translatorData);
					// }
				} catch (error) {
					console.error('Error fetching data:', error);
				}
			}
		};
		// if(selectedEmployee?.user?.id && hrDetails && employeeState === "existing_employee") {
		// 	setSubmittedForms(prev => ({
		// 		...prev,
		// 		human_resource_data_submitted: true,
		// 	}));
		// }
		fetchData();
	}, [selectedEmployee]);
	const [newEmployeeCreated, setNewEmployeeCreated] = useState(false);
	const [editSupplementB, setEditSupplementB] = useState(false);
	const [employeeState, setEmployeeState] = useState('new_employee');
	const defaultHRValues = hrDetails
		? {
				employer_title: hrDetails?.employer_title || '',
				employer_name: hrDetails?.employer_name || '',
				employer_business_name: hrDetails?.employer_business_name || '',
				employer_business_address: hrDetails?.address || '',
				employer_city: hrDetails?.city || '',
				employer_state: hrDetails?.state || '',
				employer_zip_code: hrDetails?.zip_code || '',
				employer_ein: hrDetails?.employer_ein || '',
				employer_signature_path: hrDetails?.signature_file?.path || '',
				employer_signature: hrDetails?.signature_approved || false,
				employer_signature_id: hrDetails?.signature_file?.id || 0,
			}
		: {};

	const defaultPciValues = pciFormDetails
		? {
				reviewed_acceptable_use_policy: pciFormDetails?.reviewed_acceptable_use_policy ?? false,
				reviewed_credit_card_policy: pciFormDetails?.reviewed_credit_card_policy ?? false,
				reviewed_security_policy: pciFormDetails?.reviewed_security_policy ?? false,
				signature_approved: pciFormDetails?.signature_approved ?? false,
				signature_date: formatDateToMMDDYYYY(pciFormDetails?.signature_date) ?? '',
				human_resources_signature_date:
					formatDateToMMDDYYYY(pciFormDetails?.human_resources_signature_date) ?? '',
			}
		: {};

	const defaultw4Values = W4FormDetails
		? {
				multiple_jobs_or_spouse_works: W4FormDetails?.multiple_jobs_or_spouse_works ?? 0,
				filing_status: W4FormDetails?.filing_status ?? 0,
				head_of_household: W4FormDetails?.head_of_household ?? 0,
				number_of_children: W4FormDetails?.number_of_children ?? 0,
				number_of_dependents: W4FormDetails?.number_of_dependents ?? 0,
				other_income: W4FormDetails?.other_income ?? 0,
				deductions: W4FormDetails?.deductions ?? 0,
				extra_withholding: W4FormDetails?.extra_withholding ?? 0,
				signature_approved: W4FormDetails?.signature_approved ?? false,
				signature_date: formatDateToMMDDYYYY(W4FormDetails?.signature_date) ?? '',
				human_resources_signature_date: W4FormDetails?.human_resources_signature_date ?? '',
			}
		: {};

	const commonValues = {
		signature_date: formatDateToMMDDYYYY(now),
		preparer_signature_date: formatDateToMMDDYYYY(now),
		employer_signature_date: formatDateToMMDDYYYY(now),
		multiple_jobs_or_spouse_works: 0,
		number_of_children: 0,
		number_of_dependents: 0,
		immigration_status: '1',
		head_of_household: 0,
		translator_assisted: false,
		employee_rehire: false,
		restaurant_name: workplace?.name || '',
	};

	const i9FormValue = {
		document_title: i9FormDetails?.employee_form_i9_supplement[0]?.document_title || '',
		document_number: i9FormDetails?.employee_form_i9_supplement[0]?.document_number || '',
		expiration_date:
			formatDateToMMDDYYYY(i9FormDetails?.employee_form_i9_supplement[0]?.expiration_date) || '',
		rehire_date:
			formatDateToMMDDYYYY(i9FormDetails?.employee_form_i9_supplement[0]?.rehire_date) || '',
		authorization_expiration_date:
			formatDateToMMDDYYYY(i9FormDetails?.authorization_expiration_date) || '',
		first_date_employment:
			formatDateToMMDDYYYY(i9FormDetails?.first_date_employment) ||
			formatDateToMMDDYYYY(new Date()),
	};

	useEffect(() => {
		if (i9FormDetails) {
			setEditSupplementB(!!i9FormDetails.employee_form_i9_supplement?.length);
		}
	}, [i9FormDetails]);

	const translateValue = {
		preparer_first_name: translatorDetails?.first_name || '',
		preparer_middle_initial: translatorDetails?.middle_initial || '',
		preparer_last_name: translatorDetails?.last_name || '',
		preparer_address: translatorDetails?.address || '',
		preparer_city: translatorDetails?.city || '',
		preparer_state: translatorDetails?.state || '',
		preparer_zip_code: translatorDetails?.zip_code || '',
	};

	const initialValues = {
		...commonValues,
		...defaultHRValues,
		...translateValue,
		...i9FormValue,
		...defaultw4Values,
		...defaultPciValues,
	};

	const formik = useFormik({
		initialValues,
		validate: (_values) => {
			const errors = {};
			return errors;
		},
		onSubmit: (values) => {
			alert(JSON.stringify(values, null, 2));
		},
	});

	const resetForm = (newValues: any) =>
		formik.resetForm({ values: { ...initialValues, ...newValues } });

	const recordFormSubmission = (formId) => {
		setLoadedFormIds((prev) => ({ ...prev, [formId]: true }));
	};

	const resetDocumentContext = (newState?: string) => {
		if (!newState) {
			setEmployeeState('new_employee');
		}
		setTranslatorSignatureFile(null);
		formik.resetForm();
		setNewEmployee({
			firstName: '',
			middleName: '',
			lastName: '',
			email: '',
		});
		setSubmittedForms({
			common_fields_submitted: false,
			i9_form_submitted: false,
			i9_supplement_a_submitted: false,
			i9_supplement_b_submitted: false,
			w4_form_submitted: false,
			notice_tipped_employees_submitted: false,
			mi_w4_form_submitted: false,
			food_report_agreements_submitted: false,
			w2_consent_form_submitted: false,
			pci_acknowledgment_submitted: false,
			direct_deposit_form_submitted: false,
			full_variable_time_form_submitted: false,
			i_understand_form_submitted: false,
			human_resource_data_submitted: false,
			select_employee_submitted: false,
		});
	};

	return (
		<Formik initialValues={initialValues} onSubmit={console.log} enableReinitialize={true}>
			<DocumentContext.Provider
				value={{
					formik,
					resetForm,
					isEmployer,
					selectedEmployee,
					setSelectedEmployee,
					loadedFormIds,
					setLoadedFormIds,
					recordFormSubmission,
					newEmployee,
					setNewEmployee,
					newEmployeeCreated,
					setNewEmployeeCreated,
					handleModalClose,
					open,
					setOpen,
					handleOpen,
					startDate,
					setStartDate,
					endDate,
					setEndDate,
					businessWeekStart,
					setBusinessWeekStart,
					scheduleView,
					setScheduleView,
					activeTabIndex,
					setActiveTabIndex,
					submittedForms,
					setSubmittedForms,
					employeeState,
					setEmployeeState,
					translatorSignatureFile,
					setTranslatorSignatureFile,
					editSupplementB,
					setEditSupplementB,
					resetDocumentContext,
					lastLoadedEmployeeId,
					setLastLoadedEmployeeId,
				}}
			>
				{children}
			</DocumentContext.Provider>
		</Formik>
	);
};
